import { Badge, Blockquote, Fieldset, Paper, Select, Skeleton, Text, Title, Tooltip, TypographyStylesProvider } from "@mantine/core";
import { IconExclamationCircle } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { AudienceGeoCard } from "shared/components/message/AudienceGeoCard";
import { ComposeFormProvider, createComposeFormState, useComposeForm } from "shared/components/message/compose/context";
import { EscalationEditor } from "shared/components/message/compose/EscalationEditor";
import { MessageNotes } from "shared/components/message/MessageNotes";
import { CmOverrides } from "shared/components/message/MessageOptionsEditor";
import { useLanguages } from "shared/components/settings/profile";
import { features } from "shared/graphql/features";
import { useUser } from "shared/graphql/users";
import { catching, titlecase, to } from "shared/utils/fns";
import { maybe, some } from "shared/utils/maybe";
import { mdToHtml } from "shared/utils/md";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Cm, Language, MessageSeverity } from "src/gql/graphql";

const Page = () => {
    const { id } = Route.useParams();
    const { user } = useUser();
    const { languages } = useLanguages();
    const { data } = useSuspenseQuery(query(id));

    const hasTopics = features.enabled("HAS_TOPICS");
    const hasCmOverrides = features.enabled("HAS_CM_DELIVERY_OVERRIDES");

    const form = useComposeForm({ initialValues: createComposeFormState(data) });

    const [language, setLanguage] = useState(user?.language ?? Language.English);
    const locale = data.locales.find((it) => it.language == language) ?? data;

    const [innerBody, setInnerBody] = useState("");
    useEffect(() => {
        void mdToHtml(locale.richBody).then((safeHtml) => setInnerBody(safeHtml));
    }, [locale.richBody]);

    return (
        <>
            <Content paper>
                <Content.Heading backable>
                    <Title order={3}>Sent Details</Title>
                </Content.Heading>

                <div>
                    <Title order={4}>Sender:</Title>
                    <Text fw={600}>{data.sender.name}</Text>
                </div>

                <div>
                    <Title order={4}>Criticality:</Title>
                    <Text fw={600}>Urgent = {data.severity == MessageSeverity.Urgent ? "Yes" : "No"}</Text>
                </div>

                <div>
                    <Title order={4}>Acknowledgable:</Title>
                    <Text fw={600}>{data.acknowledgable ? "Yes" : "No"}</Text>
                </div>

                <Text fw={600}>Escalations</Text>

                <ComposeFormProvider form={form}>
                    <Fieldset variant="unstyled" disabled>
                        <EscalationEditor mode="widget" />
                    </Fieldset>
                </ComposeFormProvider>

                <Title order={4}>Audience</Title>

                <Text fw={600}>Users</Text>
                {some(data.audience.users)
                    .if((it) => it.length > 0)
                    ?.take((it) => it.map(to("name")).join(", ")) ?? (
                    <Text size="sm" fs="italic">
                        No Users
                    </Text>
                )}

                <Text fw={600}>Groups</Text>
                {some(data.audience.groups.map((each) => <Badge key={each.id}>{each.name}</Badge>)).takeIf((it) => it.length > 0) ?? (
                    <Text size="sm" fs="italic">
                        No Groups
                    </Text>
                )}

                {hasTopics && (
                    <>
                        <Text fw={600}>Topics</Text>
                        {some(data.audience.topics.map((each) => <Badge key={each.id}>{each.name}</Badge>)).takeIf(
                            (it) => it.length > 0,
                        ) ?? (
                            <Text size="sm" fs="italic">
                                No Topics
                            </Text>
                        )}
                    </>
                )}

                <Text fw={600}>Geolocation</Text>
                <ComposeFormProvider form={form}>
                    {form.values.audience.geofilters.length == 0 && form.values.audience.features.length == 0 ? (
                        <Text size="sm" fs="italic">
                            No Geolocation
                        </Text>
                    ) : (
                        <AudienceGeoCard widget />
                    )}
                </ComposeFormProvider>

                <Title order={4}>Message</Title>
                <Select
                    searchable
                    data={languages.map((value) => ({
                        value,
                        label: titlecase(value) + (value == data.publishedBy?.language ? " (original composed)" : ""),
                    }))}
                    defaultValue={language}
                    label="Language"
                    renderOption={({ option }) => (
                        <Text size="sm">
                            {titlecase(option.value)}
                            {option.value == data.publishedBy?.language && <em> (original composed)</em>}
                        </Text>
                    )}
                    onChange={(_, { value }) => setLanguage(value as Language)}
                />

                <Title order={4}>Sent As</Title>
                {hasCmOverrides && (
                    <ComposeFormProvider form={form}>
                        <CmOverrides mode="widget" />
                    </ComposeFormProvider>
                )}

                <Title order={5}>Subject:</Title>
                <Text>{locale.subject}</Text>
                <Paper withBorder shadow="sm" p="md">
                    <Title order={5}>Body:</Title>
                    <article style={{ wordBreak: "break-word" }}>
                        {catching(() => (
                            <TypographyStylesProvider>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: innerBody,
                                    }}
                                />
                            </TypographyStylesProvider>
                        )) ?? (
                            <Blockquote
                                m="md"
                                icon={
                                    <Tooltip label="Viewing raw message content">
                                        <IconExclamationCircle size={16} />
                                    </Tooltip>
                                }
                                variant="warning"
                                cite="Failed to parse message body"
                            >
                                {locale.richBody}
                            </Blockquote>
                        )}
                    </article>
                </Paper>

                {maybe(locale.customizations.find((it) => it.method == Cm.Phone && it.enabled))?.take((it) => (
                    <>
                        <Title order={5}>Customized Voice:</Title>
                        <Text>{it.value}</Text>
                    </>
                ))}

                {maybe(locale.customizations.find((it) => it.method == Cm.Sms && it.enabled))?.take((it) => (
                    <>
                        <Title order={5}>Customized SMS:</Title>
                        <Text style={{ whiteSpace: "pre-wrap" }}>{it.value}</Text>
                    </>
                ))}
            </Content>
            <Content mb="md">
                <MessageNotes msgId={id} language={language} mode="widget" />
            </Content>
        </>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: qk("messages", "sentDetails", id),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query MessageSentDetails($id: UUID!) {
                        message(id: $id) {
                            publishedBy {
                                language
                            }
                            expireAt
                            ...ComposeDetails
                            sender {
                                name
                                group {
                                    name
                                }
                            }
                        }
                    }
                `),
                { id },
            ),
        select: to("message"),
    });

/** @public */
export const Route = createFileRoute("/_auth/messages/$id/details")({
    component: Page,
    pendingComponent: () => <Skeleton />,
});
